import React, { useEffect, useState } from 'react';
import { Modal } from '@ofself/shell';
import api from '../../services/api';
import { useToast } from '../../components/common';
import Inspector, { Band, Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { AppFace } from '../../components/nucleus/cells';
import CruxPanel from '../../components/CruxPanel/CruxPanel';
import { usePlace } from '../../nucleus/place';
import { Line } from './AppBoard';
import { plainSummary } from '../activity/activityData';
import {
    SHELVES, shelfOf, priceOf, asksByKind, makerOf, peopleWord, statusOf, grantHref, appsApi, loadAgentGrants, loadDeclaredAgents, agentNames,
    opsLine, runsLine,
} from './appsData';
import { useSpaceAccess } from '../../nucleus/access';
import { ago, dateOf, plural } from '../../nucleus/format';
import { statusWord } from '../../components/nucleus/status';
import StatusPill from '../../components/nucleus/StatusPill';

// ONE APP, wherever it is open (Connected, Store), read in the order you'd ask.
// Yours: what it did in the last 30 days (its operations in one line, and its
// plugin runs when there were any), its sign-ins, what it did last, and its contract here (its realm;
// what that holds is the contract's page to show). In the store: who uses it, what it costs, what it
// asks for by name (read or write, and why), read only — Connect is what opens the consent page.
// Then its agents, and who made it. Every act on it
// is in the one ActionBar.
export default function AppInspector({ entry, usage, days = 30, onClose, onChanged }) {
    const toast = useToast();
    const { groupId, spaceName, to, navigate } = usePlace();
    // a member sees an admin's acts greyed, with the same why as the cards' (AppsSection useAppsGate)
    const access = useSpaceAccess();
    const isAdmin = access.isAdmin;
    const adminHint = access.onlyAdmin('change its apps');
    const off = isAdmin ? {} : { disabled: true, hint: adminHint };
    const [crux, setCrux] = useState(null);
    const [agents, setAgents] = useState([]);
    const [roster, setRoster] = useState([]);
    const app = entry.app;
    const row = entry.row;
    const status = row ? statusOf(row) : null;
    const connected = status === 'active' || status === 'paused';
    const lapsed = status === 'expired';

    useEffect(() => {
        let live = true;
        loadAgentGrants(groupId).then((list) => {
            if (live) setAgents(list.filter((ep) => String(ep.entity_id) === String(app.id)));
        });
        loadDeclaredAgents(groupId).then((list) => { if (live) setRoster(list); });
        return () => { live = false; };
    }, [groupId, app.id]);

    // what it did last (its five newest entries in the ledger): only for an app you have
    const [recent, setRecent] = useState(undefined);
    useEffect(() => {
        if (!row) return undefined;
        let live = true;
        // the space's own ledger: in a group, the group's (every member's work with
        // this app), never your personal feed — and no facets: five rows need none
        api.getActivityFeed({ via: String(app.id), per_page: '5', include_facets: 'false' }, groupId).then((r) => { if (live) setRecent(r?.entries || []); }).catch(() => { if (live) setRecent([]); });
        return () => { live = false; };
    }, [app.id, groupId, !!row]); // eslint-disable-line react-hooks/exhaustive-deps

    const readCrux = async () => {
        setCrux({ loading: true });
        const d = await api.getCrux(app.id).catch(() => null);
        setCrux({ doc: d?.crux || null });
    };
    const done = async (fn, message) => {
        await fn();
        if (message) toast.success(message);
        onChanged?.();
    };
    const price = priceOf(app);
    const owes = price && !app.is_purchased;
    const go = (href) => { window.location.href = href; };

    const actions = [];
    if (status === 'active') {
        actions.push({ id: 'open', label: 'Open', primary: true, disabled: !app.website_url, hint: app.website_url ? undefined : 'It has no web address', run: () => window.open(app.website_url, '_blank', 'noopener') });
    }
    if (status === 'paused') {
        actions.push({ id: 'resume', label: 'Resume', primary: true, ...off, run: () => done(() => appsApi.resume(groupId, row.ep), `${app.name} is running again in ${spaceName}.`) });
    }
    if (lapsed) {
        actions.push({ id: 'renew', label: 'Renew', primary: true, disabled: !isAdmin || !app.client_id, hint: isAdmin ? undefined : adminHint, run: () => go(grantHref(app, groupId)) });
    }
    if (!row) {
        actions.push({
            id: 'connect', label: owes ? `Get · ${price}` : 'Connect', primary: true,
            disabled: !isAdmin || !app.client_id,
            hint: !isAdmin ? adminHint : undefined,
            run: () => go(grantHref(app, groupId)),
        });
        if (app.website_url) actions.push({ id: 'site', label: 'Website', run: () => window.open(app.website_url, '_blank', 'noopener') });
    }
    if (connected) {
        actions.push({ id: 'edit', label: 'Edit access', ...off, run: () => go(grantHref(app, groupId, `&edit_auth_id=${row.ep.id}`)) });
    }
    if (status === 'active') {
        actions.push({ id: 'pause', label: 'Pause', ...off, run: () => done(() => appsApi.pause(groupId, row.ep), `${app.name} is paused in ${spaceName}.`) });
    }
    if (connected) {
        actions.push({
            id: 'disconnect', label: 'Disconnect', danger: true, ...off,
            confirm: {
                title: `Disconnect ${app.name}?`,
                body: `${app.name} loses its reach into ${spaceName}.${app.is_purchased ? ' A paid subscription is cancelled with it.' : ''} It asks again next time; ${groupId ? `${spaceName}’s` : 'your'} data stays where it is.`,
            },
            run: () => done(() => appsApi.disconnect(groupId, row.ep), `${app.name} is disconnected from ${spaceName}.`),
        });
    }

    const shelf = SHELVES.find((s) => s.id === shelfOf(app));
    const lead = String(app.description || '').split(/(?<=[.!?])\s/)[0] || '';
    const more = app.description && app.description.trim() !== lead.trim() ? app.description : null;
    const realms = row?.realms?.length ? row.realms : row?.realm ? [row.realm] : [];
    const priceWord = price || 'Free';
    const kinds = asksByKind(app);
    const u = usage;
    const contractHref = to('realms', 'profiles', `open=app:${app.id}:`);

    return (
        <Inspector
            face={<AppFace app={app} />}
            tag={['App', shelf?.label, status && statusWord(status, 'app')].filter(Boolean).join(' · ')}
            title={app.name}
            sub={lead || undefined}
            actions={actions}
            onClose={onClose}
        >
            {row && u && (
                <Band title={`Last ${days} days`}>
                    <p className="apps-ops">{opsLine(u)}</p>
                    {runsLine(u) && <p className="apps-ops">{runsLine(u)}</p>}
                    <div className="apps-inspector-line"><Line days={u.by_day} legend tall /></div>
                    {/* WHAT DID NOT GO THROUGH, as a door to the entries themselves:
                        the Log, narrowed to this app and to what was refused or
                        failed, over the same 30 days */}
                    {u.errors > 0 && (
                        <button type="button" className="nx-link apps-failed" onClick={() => navigate(to('activity', 'log', `via=${app.id}&outcome=refused,failed&range=month`))}>
                            {`${plural(u.errors, 'call')} refused or failed${u.refused * 2 > u.errors ? ': it asked for more than its contract allows' : ''} · See why`}
                        </button>
                    )}
                </Band>
            )}

            {/* ONE LINE: where it is signed in, and when last. Each device is
                Security's to list (and to sign out), not the app's. */}
            {row && !groupId && u && (u.sign_ins > 0 || u.sessions_live > 0) && (
                <Band title="Sign-ins" aside={<button type="button" className="nx-link" onClick={() => navigate(to('self', 'security'))}>Security</button>}>
                    <Note>{[
                        u.sessions_live ? `Signed in on ${plural(u.sessions_live, 'device')}` : 'Not signed in now',
                        u.last_sign_in && `Last sign-in ${ago(u.last_sign_in)}`,
                    ].filter(Boolean).join(' · ')}</Note>
                </Band>
            )}

            {row && (
                <Band title="Recent" aside={<button type="button" className="nx-link" onClick={() => navigate(to('activity', 'log', `via=${app.id}`))}>All activity</button>}>
                    {recent === undefined && <Note>Reading…</Note>}
                    {recent && recent.length === 0 && <Note>Nothing yet.</Note>}
                    {(recent || []).map((e) => (
                        <Row key={e.id} name={plainSummary(e)} end={ago(e.timestamp)} onPress={() => navigate(to('activity', 'log', `open=${e.id}`))} />
                    ))}
                </Band>
            )}

            {/* ITS CONTRACT, AS ONE DOOR: its realms, since when, when it ends
                (an app's contract never does unless someone gave it a date) and
                its price, opening the contract; how it stands is the band's tag. */}
            {row ? (
                <Band title="Its contract" aside={<StatusPill status={status} of="contract" />}>
                    <Row
                        name={realms.map((r) => r.name).join(' + ') || 'No realm'}
                        sub={[
                            row.ep?.created_at && `Since ${dateOf(row.ep.created_at)}`,
                            row.ep?.expires_at ? `${lapsed ? 'Ended' : 'Ends'} ${dateOf(row.ep.expires_at)}` : 'Never ends',
                            u?.paid ? `${priceWord}, ${u.paid} paid so far` : priceWord,
                        ].filter(Boolean).join(' · ')}
                        end="Open"
                        onPress={() => navigate(contractHref)}
                    />
                </Band>
            ) : (
                <>
                    <Band title="At a glance">
                        <Facts items={[
                            { label: 'People', value: peopleWord(app) || '—' },
                            { label: 'Price', value: priceWord },
                            shelf && { label: 'Category', value: shelf.label },
                            (app.is_verified || app.is_builtin) && { label: 'Verified', value: app.is_builtin ? 'Made by ofSelf' : 'Yes' },
                            app.created_at && { label: 'Listed', value: dateOf(app.created_at) },
                            app.updated_at && { label: 'Updated', value: dateOf(app.updated_at) },
                        ]} />
                    </Band>
                    {/* WHAT IT ASKS FOR, BY NAME: each kind, read or write, and
                        why where the app says. Read only — nothing is granted
                        here; Connect opens the consent page, where you choose. */}
                    <Band title="It asks for" aside={kinds.length || undefined}>
                        <div className={`apps-preview${kinds.length > 5 ? ' is-capped' : ''}`} aria-label="What it asks for">
                            {kinds.length === 0 && <Note>Nothing in your graph.</Note>}
                            {kinds.map((k) => (
                                <Row key={k.name} name={k.name} sub={k.reasons.join(' ') || undefined} end={k.write ? 'write' : 'read'} endTone={k.write ? 'warn' : undefined} />
                            ))}
                        </div>
                        <Note>Nothing is granted until you connect and choose what it gets on the consent page.</Note>
                    </Band>
                </>
            )}

            {agents.length > 0 && (
                <Band title="Its agents here" aside={agents.length}>
                    {agents.map((ep) => (
                        <Row
                            key={ep.id}
                            name={agentNames(roster)(ep)}
                            sub={[app.name, ep.privacy_realm?.name].filter(Boolean).join(' · ')}
                            end={ep.is_active === false ? 'paused' : 'allowed'}
                            endTone={ep.is_active === false ? 'warn' : undefined}
                            onPress={() => navigate(to('apps', 'agents', `open=${ep.id}`))}
                        />
                    ))}
                </Band>
            )}

            <Band title="About" aside={<span className="nx-band__links">
                {/* the bar already carries it: Website in the store, Open on a
                    running app; only a paused or lapsed one needs the link here */}
                {row && status !== 'active' && app.website_url && <a className="nx-link" href={app.website_url} target="_blank" rel="noopener noreferrer">Website</a>}
                <button type="button" className="nx-link" onClick={readCrux}>Design doc</button>
            </span>}>
                {more && <Note>{more}</Note>}
                {!app.description && <Note>Its developer has not said yet.</Note>}
                {makerOf(app) && <Note>By {makerOf(app)}</Note>}
            </Band>

            <Modal open={!!crux} onClose={() => setCrux(null)} title={`${app.name} · Design doc`} size="tall">
                {crux?.loading ? <Note>Reading…</Note> : <CruxPanel crux={crux?.doc} appName={app.name} mode="view" />}
            </Modal>
        </Inspector>
    );
}
